import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserContext from './useUserContext';
import { getWatchPartyById } from '../services/watchService';

const useWatchPartyParticipants = (watchPartyId: string) => {
  const { user, socket } = useUserContext();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<string[]>([]);
  const [host, setHost] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle new participant joining
  const handleNewParticipantsUpdate = (data: { user: string }) => {
    setParticipants(prev => {
      if (!prev.includes(data.user)) {
        return [...prev, data.user];
      }
      return prev;
    });
  };

  // Handle participant leaving
  const handleParticipantLeftUpdate = (data: { user: string }) => {
    setParticipants(prev => prev.filter(participant => participant !== data.user));
  };
  useEffect(() => {
    // Fetch initial participants from the server when hook mounts
    const fetchInitialParticipants = async () => {
      try {
        const watchParty = await getWatchPartyById(watchPartyId);
        const participantsList = watchParty.chat.participants || [];

        // Ensure current user is in the participants list
        if (!participantsList.includes(user.username)) {
          participantsList.push(user.username);
        }

        setParticipants(participantsList);
        if (participantsList.length > 0) {
          setHost(participantsList[0]); // First participant is set as host
        }
      } catch (err) {
        setError('Error fetching watch party data');
      }
    };

    fetchInitialParticipants();

    // Listen for socket events to handle participant updates
    socket.on('newParticipant', handleNewParticipantsUpdate);
    socket.on('removeParticipant', handleParticipantLeftUpdate);

    // Sync participants when the list is updated on the server
    socket.on('participantsUpdated', (updatedParticipants: string[]) => {
      setParticipants(updatedParticipants);
    });
    socket.emit('joinWatchParty', watchPartyId, user.username);
    socket.emit('addParticipantToWatchParty', watchPartyId, user.username);

    socket.on('watchPartyEnded', () => {
      // eslint-disable-next-line no-alert
      alert('The watch party has ended');
      navigate('/watch'); // Navigate to the appropriate page
    });

    return () => {
      socket.off('newParticipant', handleNewParticipantsUpdate);
      socket.off('removeParticipant', handleParticipantLeftUpdate);
      socket.off('participantsUpdated');
      socket.off('watchPartyEnded');
    };
  }, [navigate, socket, user.username, watchPartyId]);

  const handleParticipantLeft = (data: { watchPartyId: string; user: string }) => {
    setParticipants(prev => prev.filter(participant => participant !== data.user));
    socket.emit('removeParticipantFromWatchParty', data.watchPartyId, data.user);
  };

  return { participants, host, error, handleParticipantLeft };
};

export default useWatchPartyParticipants;
